import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { BaseService } from '@services';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService } from '@auth';

import { Applicatie as ApplicatieInfo } from '../../models/applicatie.interface';

import { ToastService } from './toast.service';

export type ApplicatieVersie = '20-05-2025' | 'AANVULLEN';

@Injectable({
  providedIn: 'root',
})
export class ApplicatieInfoService extends BaseService<ApplicatieInfo> {
  protected readonly documentPath = 'applicatie';
  private readonly APP_VERSIE_SLEUTEL = 'AppVersie';

  private applicatieInfo = new BehaviorSubject<ApplicatieInfo | undefined>(undefined);

  get isGeïnitieerd(): boolean {
    return !!this.applicatieInfo.getValue();
  }

  get huidigeVersie$(): Observable<ApplicatieVersie | undefined> {
    return this.applicatieInfo.pipe(
      map((info) => info?.versie as ApplicatieVersie | undefined),
      distinctUntilChanged()
    );
  }

  constructor(
    protected db: Firestore,
    protected authService: AuthService,
    protected toastService: ToastService
  ) {
    super(db, toastService);
    this.authService.isGeauthenticeerd$.subscribe((_) => {
      this.getApplicatieData();
    });
  }

  /**
   * Haalt de lokaal opgeslagen versie op
   */
  getLocaleVersie(): ApplicatieVersie | undefined {
    return (localStorage.getItem(this.APP_VERSIE_SLEUTEL) as ApplicatieVersie) || undefined;
  }

  /**
   * Controleert op updates en forceert een refresh als er een update beschikbaar is
   * @returns true als er een update is uitgevoerd, false als er geen update nodig was
   */
  controleerEnForceerUpdate(): boolean {
    if (!this.isUpdateNodig()) {
      return false;
    }

    // Update lokale versie en herlaad pagina
    this.setLocaleVersie(this.applicatieInfo.getValue()?.versie as ApplicatieVersie);
    window.location.reload();
    return true;
  }

  /**
   * Slaat de versie op in lokale opslag
   */
  private setLocaleVersie(versie: ApplicatieVersie | undefined): void {
    if (versie) {
      localStorage.setItem(this.APP_VERSIE_SLEUTEL, versie);
    }
  }

  /**
   * Haalt de applicatie-informatie op uit Firestore
   */
  private getApplicatieData(): void {
    this.getAll().subscribe((data) => {
      if (data.length > 1) {
        throw Error('Applicatie info mag nooit meer dan een item in de collectie zijn');
      }

      if (data.length === 0) {
        throw Error('Applicatie info is niet geconfigureerd');
      }

      this.applicatieInfo.next(data[0]);
    });
  }

  /**
   * Controleert of een update nodig is
   */
  private isUpdateNodig(): boolean {
    const localeVersie = this.getLocaleVersie();
    const serverVersie = this.applicatieInfo.getValue()?.versie as ApplicatieVersie | undefined;
    return !!serverVersie && localeVersie !== serverVersie;
  }
}
